import React, { useState } from 'react';
import { Paper, Box, Typography, IconButton, Dialog, DialogContent, Tooltip, alpha } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';

// Generic card + expand-to-dialog shell for any manager-dashboard statistics
// page. `children` is a render-prop `(expanded: boolean) => ReactNode` so the
// same chart can render a compact in-grid version and a larger dialog version.
const DashboardCard = ({ title, subtitle, icon: Icon, color = '#2a78d6', children }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <>
            <Paper
                elevation={0}
                onClick={() => setExpanded(true)}
                sx={{
                    p: 2.5,
                    height: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    border: '1px solid rgba(11,11,11,0.08)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    background: `linear-gradient(180deg, ${alpha(color, 0.06)} 0%, #ffffff 90px)`,
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': {
                        boxShadow: `0 10px 28px ${alpha(color, 0.18)}`,
                    },
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        {Icon && (
                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: '50%',
                                    bgcolor: alpha(color, 0.14),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Icon sx={{ color, fontSize: 22 }} />
                            </Box>
                        )}
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
                            {subtitle && (
                                <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
                            )}
                        </Box>
                    </Box>
                    <Tooltip title="הגדלה">
                        <IconButton size="small" sx={{ color: alpha(color, 0.7), '&:hover': { bgcolor: alpha(color, 0.1) } }}>
                            <OpenInFullIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                {children(false)}
            </Paper>

            <Dialog
                open={expanded}
                onClose={() => setExpanded(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { bgcolor: '#fafafa', borderRadius: 3, overflow: 'hidden' } }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 2.5,
                        py: 1.75,
                        background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.75)} 100%)`,
                        color: '#fff',
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.5}>
                        {Icon && (
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(255,255,255,0.18)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Icon sx={{ color: '#fff', fontSize: 20 }} />
                            </Box>
                        )}
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
                            {subtitle && (
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>{subtitle}</Typography>
                            )}
                        </Box>
                    </Box>
                    <IconButton size="small" onClick={() => setExpanded(false)} sx={{ color: '#fff' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 3 }}>{expanded && children(true)}</DialogContent>
            </Dialog>
        </>
    );
};

export default DashboardCard;
